import { Component, h, Prop, Element } from '@stencil/core';

/**
 * `me-text` is a text component for displaying inline or block text
 * with different visual styles (variant, weight, and alignment).
 */

const COMPONENT_NAME = 'me-text';
const DEFAULTS = {
  tag: 'p',
  variant: 'regular',
  weight: 'normal',
  align: 'left',
} as const;

/**
 * @example
 *   <me-text tag="p" variant="large">Our platform brings together everything you need to grow your business.</me-text>
 * @slot - {required}, {content}, string
 */
@Component({
  tag: 'me-text',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Text {
  /** Reference to the host element (<me-text>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag to be rendered (`p`, `span`, or `div`).
   * @default "p"
   */
  @Prop() tag: 'p' | 'span' | 'div' = DEFAULTS.tag;

  /**
   * Visual variant (typically font size or spacing) of the text.
   * Maps to specific utility classes like `me-text--small`, etc.
   * @default "regular"
   */
  @Prop() variant: 'tiny' | 'small' | 'regular' | 'medium' | 'large' = DEFAULTS.variant;

  /**
   * Font weight for the text content.
   * @default "normal"
   */
  @Prop() weight: 'extra-bold' | 'bold' | 'semi-bold' | 'medium' | 'normal' | 'light' = DEFAULTS.weight;

  /**
   * Text alignment within the container.
   * @default "left"
   */
  @Prop() align: 'left' | 'right' | 'center' = DEFAULTS.align;

  /**
   * Raw HTML content rendered via innerHTML.
   * When provided, renders the HTML string directly instead of projecting slot children.
   * Maps to `raw-html` attribute — use this in JSON payloads instead of `innerHTML`.
   */
  @Prop() rawHtml?: string;

  render() {
    // Use the specified tag (p, span, or div) for rendering.
    // When rawHtml is provided, default to 'div' to avoid invalid nesting
    // (rawHtml typically contains block elements like <p>, <ul> etc.)
    const Tag = this.rawHtml && this.tag === DEFAULTS.tag ? 'div' : this.tag;

    // Only apply class modifiers if the value differs from the default
    const variantClass = this.variant !== DEFAULTS.variant ? `${COMPONENT_NAME}--${this.variant}` : '';

    const weightClass = this.weight !== DEFAULTS.weight ? `${COMPONENT_NAME}--weight-${this.weight}` : '';

    const alignClass = this.align !== DEFAULTS.align ? `${COMPONENT_NAME}--${this.align}` : '';

    // Combine all classes into one string, filtering out empty values
    const classList = [COMPONENT_NAME, variantClass, weightClass, alignClass].filter(Boolean).join(' ');

    return (
      <Tag class={classList} {...(this.rawHtml ? { innerHTML: this.rawHtml } : {})}>
        {!this.rawHtml && <slot></slot>}
      </Tag>
    );
  }
}
